import { parseServerTimestamp } from './dateService';

// A customer message sent within this long after the AI's last message is treated as part of the
// same exchange and shown without a time.
export const CUSTOMER_REPLY_TIME_GAP_MS = 5 * 60 * 1000;

const toMillis = (timestamp) => {
  const date = parseServerTimestamp(timestamp);
  return date ? date.getTime() : null;
};

/**
 * Decides which chat bubbles show their time. Returns a Set of message ids whose time is visible;
 * every other message is rendered without one.
 *
 * `messages` must be in display order. Sender values: 'user' = customer, 'handoff' = handoff notice
 * (admin-only), anything else = the AI.
 *
 *  - First AI message ................................. always shown, even if it directly replies
 *                                                       to the customer's opening message
 *  - Later AI reply to a customer message ............. hidden
 *  - AI follow-up (AI message right after an AI one) .. always shown
 *  - Customer's first message ......................... always shown
 *  - Later customer message ........................... shown only if more than `gapMs` after the
 *                                                       AI's last message; hidden within it
 *
 * Handoff notices are always shown and are skipped when deciding what came "before" a message, so
 * admins (who see them) and non-admins (who don't) get identical results for everything else.
 */
export const getMessageIdsWithVisibleTime = (messages, gapMs = CUSTOMER_REPLY_TIME_GAP_MS) => {
  const visible = new Set();
  let previousSender = null;
  let lastAiTime = null;
  let seenCustomerMessage = false;
  let seenAiMessage = false;

  (messages || []).forEach((message) => {
    if (message.sender === 'handoff') {
      visible.add(message.id);
      return;
    }

    const time = toMillis(message.timestamp);

    if (message.sender === 'user') {
      const showTime =
        !seenCustomerMessage || lastAiTime === null || time === null || time - lastAiTime > gapMs;
      if (showTime) visible.add(message.id);
      seenCustomerMessage = true;
    } else {
      // AI message: always shown the first time, regardless of what came before it. After that,
      // hidden only when it is a direct reply to a customer message.
      if (!seenAiMessage || previousSender !== 'user') visible.add(message.id);
      seenAiMessage = true;
      lastAiTime = time;
    }

    previousSender = message.sender === 'user' ? 'user' : 'ai';
  });

  return visible;
};
